import { useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { isEmpty } from 'lodash';

import { GET_USER_FABRIC_FAVORITES } from 'mocks/queries';
import {
  ADD_TO_USER_FABRIC_FAVS,
  REMOVE_FABRIC_FROM_USER_FABRIC_FAVS,
} from 'mocks/mutations';
import useUserData from 'data-hooks/useUserData';
import useAllOptions from 'data-hooks/useAllOptions';
import useIsLoggedInCallback from 'data-hooks/useUserData/useIsLoggedInCallback';

const DEFAULT_USER_FABRIC_FAVS = {};
const DEFAULT_FABRIC_FAVS_INFO = {};

const useFabricFavs = ({ loadMaterialDetails } = {}) => {
  const { allOptionsValues } = useAllOptions();

  const { loading, data: userFabricFavs, refetch } = useQuery(
    GET_USER_FABRIC_FAVORITES,
    {
      ssr: false,
    }
  );

  const { isLoggedIn, userId } = useUserData();

  const updateUserFav = useCallback((cache, updatedFabricFavData, action) => {
    const existingUserFavs = cache.readQuery({
      query: GET_USER_FABRIC_FAVORITES,
    });

    if (existingUserFavs && updatedFabricFavData) {
      cache.writeQuery({
        query: GET_USER_FABRIC_FAVORITES,
        data: {
          userFabricFavs: {
            ...updatedFabricFavData,
            ...(action === 'add' &&
              Object.keys(existingUserFavs.userFabricFavs).length &&
              existingUserFavs.userFabricFavs),
          },
        },
      });
    }
  }, []);

  const [addToUserFabricFavorites] = useMutation(ADD_TO_USER_FABRIC_FAVS, {
    update(cache, { data }) {
      const favs = data.userFabricFavs;
      Object.keys(favs).forEach(key => key === 'undefined' && delete favs[key]);
      updateUserFav(cache, favs, 'add');
    },
  });

  const [removeFromUserFabricFavorites] = useMutation(
    REMOVE_FABRIC_FROM_USER_FABRIC_FAVS,
    {
      update(cache, { data }) {
        const favs = data.userFabricFavs;
        Object.keys(favs).forEach(
          key => key === 'undefined' && delete favs[key]
        );
        updateUserFav(cache, favs, 'remove');
      },
    }
  );

  useIsLoggedInCallback(refetch);

  const fabricFavsInfo = useMemo(() => {
    if (
      !loadMaterialDetails ||
      isEmpty(userFabricFavs?.userFabricFavs) ||
      isEmpty(allOptionsValues)
    ) {
      return DEFAULT_FABRIC_FAVS_INFO;
    }

    const favFabricsInfoTemp = {};

    for (const key of Object.keys(userFabricFavs?.userFabricFavs)) {
      if (!isEmpty(allOptionsValues[key])) {
        favFabricsInfoTemp[key] = { ...allOptionsValues[key], materialId: key };
      }
    }

    return favFabricsInfoTemp;
  }, [allOptionsValues, loadMaterialDetails, userFabricFavs?.userFabricFavs]);

  const setOptionAsFav = useCallback(
    async optionId => {
      if (userFabricFavs?.[optionId]) {
        return;
      }

      if (!isLoggedIn) {
        addToUserFabricFavorites({
          variables: {
            materialId: optionId,
          },
        });
        return;
      }

      addToUserFabricFavorites({
        variables: {
          userId,
          materialId: optionId,
        },
      });
    },
    [addToUserFabricFavorites, isLoggedIn, userFabricFavs, userId]
  );

  const removeOptionAsFav = useCallback(
    async optionId => {
      if (!isLoggedIn) {
        removeFromUserFabricFavorites({
          variables: {
            materialId: optionId,
          },
        });
        return;
      }

      removeFromUserFabricFavorites({
        variables: {
          userId,
          materialId: optionId,
        },
      });
    },
    [isLoggedIn, removeFromUserFabricFavorites, userId]
  );

  return {
    data: {
      userFabricFavs: loading
        ? DEFAULT_USER_FABRIC_FAVS
        : userFabricFavs?.userFabricFavs,
      isUserLoggedIn: isLoggedIn,
      fabricFavsInfo,
    },
    actions: { setOptionAsFav, removeOptionAsFav },
  };
};

export default useFabricFavs;
